import { PointsLeaderboardEntry } from '@/domain/merkl/types'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Badge, GradientBadge } from '@/ui/atoms/badge/Badge'
import { useAccount } from '@/domain/hooks/useAccount'
import { cn } from '@/ui/utils/style'
import { useUserPoints } from '@/domain/merkl/user-points/useUserPoints'
import { useChainId } from 'wagmi'
import { useMemo, useState } from 'react'
import { Button } from '@/ui/atoms/button/Button'
import { TableCell, TableRow } from '@/ui/atoms/table/Table'
import { DataTable } from '@/ui/molecules/data-table/DataTable'
import { KNOWN_PROTOCOLS } from '@/config/consts'

interface PointsTableProps {
  pointsLeaderboard?: PointsLeaderboardEntry[]
}

const ITEMS_PER_PAGE = 50

export function PointsTable({ pointsLeaderboard }: PointsTableProps) {
  const account = useAccount()
  const chainId = useChainId()
  const { data: userPoints } = useUserPoints({ account, chainId })
  const [page, setPage] = useState(0)

  const tableData = useMemo<PointsLeaderboardEntry[]>(() => {
    if (!pointsLeaderboard) return []
    return pointsLeaderboard
  }, [pointsLeaderboard])

  const paginatedTableData = useMemo(() => {
    const start = page * ITEMS_PER_PAGE
    const end = start + ITEMS_PER_PAGE
    return tableData.slice(start, end)
  }, [tableData, page])

  const totalPages = Math.max(1, Math.ceil(tableData.length / ITEMS_PER_PAGE))

  return (
    <div className="w-full">
      {tableData.length ? (
        <div className="w-full">
          <DataTable
            gridTemplateColumnsClassName="grid-cols-[54px_1fr_1fr] sm:grid-cols-[84px_1fr_1fr]"
            columnDef={{
              ranking: {
                header: 'Rank',
                headerAlign: 'left',
                showOnMobile: true,
                sortable: true,
                sortingFn: (a, b) => a.original.ranking - b.original.ranking,
                renderCell: ({ ranking }) => (
                  <div>
                    <div className="flex w-full flex-row justify-start pl-3 text-sm">{ranking ? ranking : '😻'}</div>
                  </div>
                ),
              },
              recipient: {
                header: 'User',
                headerAlign: 'left',
                showOnMobile: true,
                renderCell: ({ recipient }) => (
                  <div>
                    <div className="flex w-full flex-col items-start gap-1 text-sm sm:flex-row sm:items-center sm:gap-4">
                      <p>
                        {recipient.slice(0, 7)}...{recipient.slice(-4)}
                      </p>
                      {recipient.toLowerCase() === account?.toLowerCase() && (
                        <GradientBadge>
                          <Typography variant="prompt" gradient>
                            You
                          </Typography>
                        </GradientBadge>
                      )}
                      {KNOWN_PROTOCOLS.includes(recipient.toLowerCase()) && (
                        <Badge>
                          <Typography variant="prompt" className="text-xs">
                            Protocol
                          </Typography>
                        </Badge>
                      )}
                    </div>
                  </div>
                ),
              },
              points: {
                header: 'Total points',
                headerAlign: 'right',
                showOnMobile: true,
                sortable: true,
                sortingFn: (a, b) => (a.original.amount.gt(b.original.amount) ? 1 : -1),
                renderCell: ({ points }, mobileViewOptions) => {
                  return (
                    <div
                      className={cn(
                        'flex items-center gap-2',
                        mobileViewOptions?.isMobileView ? 'justify-center' : 'justify-end',
                      )}
                    >
                      <Typography variant="p" gradient className="font-bold text-sm">
                        {points
                          ? Number(points).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : '-'}
                      </Typography>
                    </div>
                  )
                },
              },
            }}
            data={
              userPoints && account
                ? [
                    {
                      recipient: account,
                      amount: userPoints.amount,
                      formattedAmount: userPoints.formattedAmount,
                      ranking: 0,
                      points: userPoints.points,
                    },
                    ...paginatedTableData,
                  ]
                : paginatedTableData
            }
            footer={
              totalPages > 1 && (
                <TableRow>
                  <TableCell className="text-center">
                    <div className="flex w-full flex-row items-center justify-center gap-4 pt-4">
                      <Button variant="outline" size="sm" onClick={() => setPage(0)} disabled={page === 0}>
                        First
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                        Previous
                      </Button>
                      <Typography variant="p" className="hidden text-sm sm:block">
                        {page + 1} of {totalPages}
                      </Typography>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page === totalPages - 1}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(totalPages - 1)}
                        disabled={page === totalPages - 1}
                      >
                        Last
                      </Button>
                    </div>
                    <Typography variant="p" className="mt-4 text-sm sm:hidden">
                      {page + 1} of {totalPages}
                    </Typography>
                  </TableCell>
                </TableRow>
              )
            }
          />
        </div>
      ) : (
        <div className="flex w-full flex-col items-center justify-center gap-2 py-10 text-center">
          <Typography variant="prompt" className="text-sm">
            Leaderboard is currently empty. Check back soon!
          </Typography>
        </div>
      )}
    </div>
  )
}
